import { InjectionToken } from '@angular/core';
import { Observable } from 'rxjs';

import { ActionConfirm } from '@shared/models/listing.model';

/** `(permission: string) => boolean`, e.g. `'5.Delete'`. */
export type ListingPermissionCheck = (permission: string) => boolean;

/**
 * Whether the signed-in user holds a permission claim.
 *
 * Defaults to "everything is permitted" so the listing package has no dependency on an
 * auth package that does not exist yet.
 *
 * WIRED: overridden in `shell.routes.ts` (NOT app.config.ts - that would pull the
 * permission graph into the login island) with the JWT-claim lookup. Without it an
 * `ActionConfig.permission` is decorative.
 */
export const LISTING_PERMISSION_CHECK = new InjectionToken<ListingPermissionCheck>(
  'LISTING_PERMISSION_CHECK',
  { factory: () => () => true },
);

/** Resolves true when the user confirms. May answer synchronously, by promise or by stream. */
export type ListingConfirmFn = (
  confirm: ActionConfirm,
  row: unknown,
) => boolean | Promise<boolean> | Observable<boolean>;

/**
 * Asks the user to confirm a destructive action.
 *
 * The default falls back to the platform dialog, which is deliberately ugly: it is a
 * placeholder, not a design.
 *
 * WIRED: `shell.routes.ts` overrides this with `DialogService.confirm()` for everything
 * under /app. This default now only applies to a listing rendered outside the shell,
 * of which there are none - keep it so a spec that forgets the provider still runs.
 */
export const LISTING_CONFIRM = new InjectionToken<ListingConfirmFn>('LISTING_CONFIRM', {
  factory: (): ListingConfirmFn => (confirm) =>
    typeof window !== 'undefined' && typeof window.confirm === 'function'
      ? window.confirm(`${confirm.title}\n\n${confirm.message}`) === true
      : false,
});

/** A one-line user-facing message. */
export type ListingNotifyFn = (message: string, tone: 'success' | 'error') => void;

/**
 * Post-delete / failed-export feedback.
 *
 * WIRED: `shell.routes.ts` overrides this with `ToastService` for everything under
 * /app. The no-op default remains so a listing rendered without the provider - in a
 * spec, say - degrades quietly instead of crashing.
 */
export const LISTING_NOTIFY = new InjectionToken<ListingNotifyFn>('LISTING_NOTIFY', {
  factory: (): ListingNotifyFn => () => undefined,
});
